// src/components/PrescriptionEligibilityModal.tsx
//
// Puerta de entrada a Recetas Electrónicas.
//
// Antes, el profesional se enteraba de que le faltaba algo (matrícula, 2FA,
// habilitación en REFEPS) recién cuando intentaba emitir, con un 403 seco.
// Este modal consulta el estado ANTES y le dice exactamente qué le falta y
// dónde resolverlo.
//
// Cuando está habilitado le muestra los datos que devuelve el registro
// nacional, para que confirme con qué identidad va a firmar: una receta es un
// documento legal y conviene que vea su matrícula antes de emitir.

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X, ShieldCheck, ShieldAlert, Loader2, FileSignature,
  IdCard, KeyRound, RefreshCw, AlertTriangle, ChevronRight,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

type Accion =
  | 'CARGAR_MATRICULA' | 'COMPLETAR_PERFIL' | 'REVISAR_MATRICULA'
  | 'REINTENTAR' | 'ACTIVAR_2FA' | null;

interface Eligibility {
  puedeEmitir: boolean;
  motivo: string;
  mensaje?: { titulo: string; detalle: string; accion: Accion };
  validacionDegradada?: boolean;
  profesional?: {
    nombre: string; matricula: string | null; jurisdiccion: string | null;
    especialidad: string | null; profesion: string | null;
    refepsId: string | null; validadaEl: string | null;
  };
  /** Snapshot crudo de REFEPS. Es la identidad con la que se firma. */
  refeps?: {
    nombreCompleto?: string | null;
    dni?: string | null;
    gender?: string | null;
    habilitada?: boolean;
    active?: boolean;
    checkedAt?: string | null;
    matricula?: MatriculaRefeps | null;
    matriculas?: MatriculaRefeps[];
  } | null;
  emision?: { habilitada: boolean; motivo: string | null };
  seguridad?: { dosPasosActivo: boolean; sugerirDosPasos: boolean };
}

interface MatriculaRefeps {
  numero?: string | null;
  issuer?: string | null;
  tipo?: string | null;
  especialidad?: string | null;
  habilitada?: boolean;
  periodo?: { start?: string | null } | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Se llama cuando el profesional está habilitado y decide continuar. */
  onContinuar: () => void;
  /** Navegación a las pantallas donde resuelve lo que le falta. */
  onIrAPerfil?: () => void;
  onIrASeguridad?: () => void;
}

const PrescriptionEligibilityModal: React.FC<Props> = ({
  isOpen, onClose, onContinuar, onIrAPerfil, onIrASeguridad,
}) => {
  const [cargando, setCargando] = useState(true);
  const [datos, setDatos] = useState<Eligibility | null>(null);
  const [error, setError] = useState<string | null>(null);

  const consultar = async () => {
    setCargando(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/prescriptions/eligibility`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok || json.success === false) {
        setError(json.error || 'No pudimos verificar tu habilitación');
      } else {
        setDatos(json);
      }
    } catch {
      setError('No pudimos conectarnos. Revisá tu conexión e intentá de nuevo.');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (isOpen) consultar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const accionar = (accion: Accion) => {
    // ACTIVAR_2FA ya no lo devuelve el backend: el 2FA dejó de ser requisito
    // (6-sep-2026). Se mantiene esta rama por la ventana de despliegue, en la
    // que el front nuevo puede convivir un rato con el backend viejo.
    if (accion === 'ACTIVAR_2FA') { onIrASeguridad?.(); onClose(); return; }
    if (accion === 'REINTENTAR') { consultar(); return; }

    // Todo lo que tenga que ver con la matrícula va a su propia pantalla y no
    // al editor de perfil completo, donde convivía con la dirección del
    // consultorio y el resto de la configuración. Ahí se busca por DNI contra
    // REFEPS y se elige la matrícula de una lista.
    window.location.href = '/professional/habilitar-recetas';
    onClose();
  };

  const etiquetaAccion = (accion: Accion) => {
    switch (accion) {
      case 'ACTIVAR_2FA': return 'Activar verificación en dos pasos';
      case 'CARGAR_MATRICULA': return 'Cargar mi matrícula';
      case 'COMPLETAR_PERFIL': return 'Completar mis datos';
      case 'REVISAR_MATRICULA': return 'Revisar mi matrícula';
      case 'REINTENTAR': return 'Reintentar';
      default: return null;
    }
  };

  const iconoAccion = (accion: Accion) => {
    if (accion === 'ACTIVAR_2FA') return <KeyRound className="w-4 h-4" />;
    if (accion === 'REINTENTAR') return <RefreshCw className="w-4 h-4" />;
    return <IdCard className="w-4 h-4" />;
  };

  const fecha = (iso?: string | null) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' }); }
    catch { return null; }
  };

  // REFEPS puede devolver más de una matrícula (una nacional y una o varias
  // provinciales). Se muestran todas: con más de una, ver cuál está en uso es
  // justamente lo que evita firmar con la equivocada.
  const matriculasRefeps: MatriculaRefeps[] = datos?.refeps?.matriculas?.length
    ? datos.refeps.matriculas
    : datos?.refeps?.matricula
      ? [datos.refeps.matricula]
      : [];

  // El registro devuelve el género en el vocabulario de FHIR.
  const generoLegible = (g?: string | null) => {
    if (!g) return null;
    const v = String(g).toLowerCase();
    if (v === 'female') return 'Femenino';
    if (v === 'male') return 'Masculino';
    return 'Otro';
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center">
      {/* Fondo */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-[fadeIn_.15s_ease-out]"
        onClick={onClose}
      />

      {/* Hoja: sube desde abajo en celular, aparece centrada en escritorio.
          max-h + scroll interno: al sumar las matrículas de REFEPS el
          contenido pasó a ser variable, y sin tope crecía más que la pantalla
          y se cortaba de arriba (el overflow-hidden lo recortaba sin dejar
          scrollear). El alto lo pone la hoja y el que scrollea es el cuerpo. */}
      <div className="relative w-full sm:max-w-md max-h-[92vh] flex flex-col bg-white rounded-t-[28px] sm:rounded-[28px] shadow-2xl overflow-hidden animate-[slideUp_.25s_cubic-bezier(.32,.72,0,1)]">
        {/* Barrita de arrastre, solo en celular */}
        <div className="sm:hidden pt-3 pb-1 flex justify-center flex-shrink-0">
          <div className="w-9 h-1 rounded-full bg-gray-300" />
        </div>

        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
          aria-label="Cerrar"
        >
          <X className="w-4 h-4 text-gray-500" />
        </button>

        <div className="px-6 pb-7 pt-5 sm:pt-7 overflow-y-auto min-h-0">
          {/* ---------- Cargando ---------- */}
          {cargando && (
            <div className="py-10 flex flex-col items-center text-center">
              <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
              <p className="mt-4 text-sm text-gray-500">Verificando tu habilitación…</p>
              <p className="mt-1 text-xs text-gray-400">Consultando el registro nacional</p>
            </div>
          )}

          {/* ---------- Error de red ---------- */}
          {!cargando && error && (
            <div className="py-6 text-center">
              <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto">
                <AlertTriangle className="w-7 h-7 text-amber-500" />
              </div>
              <h3 className="mt-4 text-lg font-semibold text-gray-900">No pudimos verificar</h3>
              <p className="mt-1.5 text-sm text-gray-500 leading-relaxed">{error}</p>
              <button
                onClick={consultar}
                className="mt-5 w-full py-3 rounded-2xl bg-gray-900 text-white text-sm font-semibold active:scale-[.98] transition-transform"
              >
                Reintentar
              </button>
            </div>
          )}

          {/* ---------- No habilitado ---------- */}
          {!cargando && !error && datos && !datos.puedeEmitir && (
            <div className="py-2 text-center">
              <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto">
                <ShieldAlert className="w-7 h-7 text-amber-500" />
              </div>
              <h3 className="mt-4 text-lg font-semibold text-gray-900">
                {datos.mensaje?.titulo || 'Todavía no podés emitir recetas'}
              </h3>
              <p className="mt-2 text-sm text-gray-500 leading-relaxed">
                {datos.mensaje?.detalle}
              </p>

              {etiquetaAccion(datos.mensaje?.accion ?? null) && (
                <button
                  onClick={() => accionar(datos.mensaje?.accion ?? null)}
                  className="mt-6 w-full py-3.5 rounded-2xl bg-indigo-600 text-white text-sm font-semibold flex items-center justify-center gap-2 active:scale-[.98] transition-transform"
                >
                  {iconoAccion(datos.mensaje?.accion ?? null)}
                  {etiquetaAccion(datos.mensaje?.accion ?? null)}
                </button>
              )}

              <button
                onClick={onClose}
                className="mt-2 w-full py-3 rounded-2xl text-sm font-medium text-gray-500 hover:bg-gray-50 transition-colors"
              >
                Ahora no
              </button>
            </div>
          )}

          {/* ---------- Habilitado ---------- */}
          {!cargando && !error && datos?.puedeEmitir && (
            <div className="py-2">
              <div className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto">
                  <ShieldCheck className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-gray-900">Estás habilitado</h3>
                <p className="mt-1.5 text-sm text-gray-700">
                  Datos según <strong>SISA · REFEPS</strong>
                </p>
              </div>

              {/* -----------------------------------------------------------
                  Lo que devuelve REFEPS, no lo que tiene cargado la cuenta.
                  Antes acá salía el nombre del usuario logueado, que puede no
                  ser el del titular de la matrícula: si una cuenta carga la
                  matrícula de otra persona, el modal decía el nombre de la
                  cuenta y firmaba con la matrícula ajena sin que se notara.
                  ----------------------------------------------------------- */}
              <div className="mt-5 rounded-2xl bg-gray-50 divide-y divide-gray-200/70">
                {[
                  ['Nombre', datos.refeps?.nombreCompleto],
                  ['DNI', datos.refeps?.dni],
                  ['Sexo', generoLegible(datos.refeps?.gender)],
                  ['Consultado el', fecha(datos.refeps?.checkedAt || datos.profesional?.validadaEl)],
                ]
                  .filter(([, v]) => Boolean(v))
                  .map(([k, v]) => (
                    <div key={String(k)} className="flex items-baseline justify-between gap-4 px-4 py-2.5">
                      <span className="text-xs text-gray-600 flex-shrink-0">{k}</span>
                      <span className="text-sm text-gray-900 font-semibold text-right">{v}</span>
                    </div>
                  ))}
              </div>

              {/* Todas las matrículas que el registro le reconoce, no solo la
                  elegida: sirve para confirmar que se está usando la correcta. */}
              {matriculasRefeps.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide px-1 mb-1.5">
                    {matriculasRefeps.length === 1 ? 'Matrícula' : `Matrículas (${matriculasRefeps.length})`}
                  </p>
                  <div className="rounded-2xl bg-gray-50 divide-y divide-gray-200/70">
                    {matriculasRefeps.map((m, i) => {
                      const esLaElegida = String(m.numero || '') === String(datos.profesional?.matricula || '');
                      return (
                        <div key={`${m.numero}-${i}`} className="px-4 py-3">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-sm font-bold text-gray-900">{m.numero}</span>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {m.habilitada && (
                                <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                                  Habilitada
                                </span>
                              )}
                              {esLaElegida && (
                                <span className="text-[11px] font-semibold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-full">
                                  En uso
                                </span>
                              )}
                            </div>
                          </div>
                          {m.issuer && (
                            <p className="text-xs text-gray-700 mt-0.5 leading-snug">{m.issuer}</p>
                          )}
                          <p className="text-xs text-gray-600 mt-0.5">
                            {[m.tipo, m.especialidad, m.periodo?.start ? `desde ${fecha(m.periodo.start)}` : null]
                              .filter(Boolean)
                              .join(' · ')}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* MSAL caído: se está usando validación en caché vencida */}
              {datos.validacionDegradada && (
                <p className="mt-3 text-[11px] leading-snug text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                  El registro nacional no responde en este momento. Estamos usando tu última
                  validación guardada.
                </p>
              )}

              {/* El canal de emisión todavía no está activo */}
              {datos.emision && !datos.emision.habilitada && (
                <p className="mt-3 text-[11px] leading-snug text-gray-600 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
                  La emisión todavía no está activa en tu cuenta. Vas a poder preparar recetas,
                  pero no enviarlas hasta que la habilitemos.
                </p>
              )}

              <button
                onClick={() => { onContinuar(); onClose(); }}
                className="mt-5 w-full py-3.5 rounded-2xl bg-emerald-600 text-white text-sm font-semibold flex items-center justify-center gap-2 active:scale-[.98] transition-transform"
              >
                <FileSignature className="w-4 h-4" />
                Ir a Recetas Electrónicas
                <ChevronRight className="w-4 h-4 -ml-1" />
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { transform: translateY(18px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
      `}</style>
    </div>,
    document.body
  );
};

export default PrescriptionEligibilityModal;
